import { PUBLIC_BACKEND } from '$app/env/public';
import { minAvailableOrLatestRangePrice } from '$lib/listing-pricing/calculate';
import type {
	BoatsResponse,
	ListingDateRangesResponse,
	ListingsResponse,
	PortsResponse,
	ReviewsResponse,
	UsersResponse
} from '@/pocketbase/pocketbase-types';
import { error } from '@sveltejs/kit';
import type { EntryGenerator, PageLoad } from './$types';

export type ProfileListing = ListingsResponse<{ boat: BoatsResponse<{ port: PortsResponse }> }> & {
	minPriceFromRanges?: number | null;
};

export type ProfileReview = ReviewsResponse<{
	reviewer: UsersResponse;
	listing: ListingsResponse;
}>;

export interface ProfilePageData {
	userProfile: UsersResponse;
	listings: ProfileListing[];
	reviews: ProfileReview[];
	isArmador: boolean;
	hasBoat: boolean;
	ratingAvg: number | null;
	ratingCount: number;
}

async function fetchUserByUsername(
	fetchFn: typeof fetch,
	username: string
): Promise<UsersResponse | null> {
	const query = new URLSearchParams({
		filter: `username = "${username}"`,
		perPage: '1'
	});
	const res = await fetchFn(`${PUBLIC_BACKEND}/api/collections/users/records?${query}`);
	if (!res.ok) return null;
	const data = (await res.json()) as { items: UsersResponse[] };
	return data.items[0] ?? null;
}

async function fetchUserListings(
	fetchFn: typeof fetch,
	userId: string
): Promise<Omit<ProfileListing, 'minPriceFromRanges'>[]> {
	const query = new URLSearchParams({
		filter: `user = "${userId}" && status = "active"`,
		expand: 'boat.port',
		perPage: '50'
	});
	const res = await fetchFn(`${PUBLIC_BACKEND}/api/collections/listings/records?${query}`);
	if (!res.ok) return [];
	const data = (await res.json()) as { items: Omit<ProfileListing, 'minPriceFromRanges'>[] };
	return data.items;
}

async function attachMinPrices(
	fetchFn: typeof fetch,
	listings: Omit<ProfileListing, 'minPriceFromRanges'>[]
): Promise<ProfileListing[]> {
	if (listings.length === 0) return [];

	const ids = listings.map((listing) => listing.id);
	const rangesFilter = ids.map((id) => `listing = "${id}"`).join(' || ');
	const query = new URLSearchParams({
		perPage: '500',
		filter: `(${rangesFilter})`
	});

	let ranges: ListingDateRangesResponse[] = [];
	try {
		const res = await fetchFn(
			`${PUBLIC_BACKEND}/api/collections/listingDateRanges/records?${query}`
		);
		if (res.ok) {
			const data = await res.json();
			ranges = data.items;
		}
	} catch (err) {
		console.error('[perfil] no se pudieron cargar rangos de precio:', err);
	}

	const rangesByListing = new Map<string, ListingDateRangesResponse[]>();
	for (const range of ranges) {
		const listingRanges = rangesByListing.get(range.listing) ?? [];
		listingRanges.push(range);
		rangesByListing.set(range.listing, listingRanges);
	}

	return listings.map((listing) => ({
		...listing,
		minPriceFromRanges: minAvailableOrLatestRangePrice(rangesByListing.get(listing.id) ?? [])
	}));
}

async function fetchUserHasBoat(fetchFn: typeof fetch, userId: string): Promise<boolean> {
	const query = new URLSearchParams({
		filter: `owner = "${userId}"`,
		perPage: '1',
		fields: 'id'
	});
	const res = await fetchFn(`${PUBLIC_BACKEND}/api/collections/boats/records?${query}`);
	if (!res.ok) return false;
	const data = (await res.json()) as { totalItems: number };
	return data.totalItems > 0;
}

async function fetchUserReviews(fetchFn: typeof fetch, userId: string): Promise<ProfileReview[]> {
	const query = new URLSearchParams({
		filter: `reviewee = "${userId}"`,
		expand: 'reviewer,listing.boat.port',
		sort: '-created',
		perPage: '50'
	});
	const res = await fetchFn(`${PUBLIC_BACKEND}/api/collections/reviews/records?${query}`);
	if (!res.ok) return [];
	const data = (await res.json()) as { items: ProfileReview[] };
	return data.items;
}

function computeRating(reviews: ProfileReview[]) {
	if (reviews.length === 0) {
		return { ratingAvg: null, ratingCount: 0 };
	}
	const total = reviews.reduce((sum, review) => sum + review.rating, 0);
	return {
		ratingAvg: total / reviews.length,
		ratingCount: reviews.length
	};
}

export const load: PageLoad = async ({ params, fetch }) => {
	const username = params.username?.trim();
	if (!username) {
		error(404, 'Perfil no encontrado');
	}

	const userProfile = await fetchUserByUsername(fetch, username);
	if (!userProfile) {
		error(404, 'Perfil no encontrado');
	}

	const [rawListings, reviews, hasBoat] = await Promise.all([
		fetchUserListings(fetch, userProfile.id),
		fetchUserReviews(fetch, userProfile.id),
		fetchUserHasBoat(fetch, userProfile.id)
	]);

	const listings = await attachMinPrices(fetch, rawListings);
	const { ratingAvg, ratingCount } = computeRating(reviews);

	return {
		userProfile,
		listings,
		reviews,
		isArmador: listings.length > 0,
		hasBoat,
		ratingAvg,
		ratingCount
	} satisfies ProfilePageData;
};

async function fetchAllUsernames(fetchFn: typeof fetch): Promise<string[]> {
	const usernames: string[] = [];
	let page = 1;
	let totalPages = 1;

	while (page <= totalPages) {
		const query = new URLSearchParams({
			fields: 'username',
			perPage: '200',
			page: String(page)
		});
		const res = await fetchFn(`${PUBLIC_BACKEND}/api/collections/users/records?${query}`);
		if (!res.ok) {
			throw new Error(`PocketBase respondió ${res.status}`);
		}
		const data = (await res.json()) as {
			items: { username: string }[];
			totalPages: number;
		};
		for (const user of data.items) {
			if (user.username) {
				usernames.push(user.username);
			}
		}
		totalPages = data.totalPages;
		page++;
	}

	return usernames;
}

/**
 * Entradas de prerender: usuarios existentes en PocketBase.
 * Perfiles nuevos post-deploy se resuelven vía fallback SPA.
 */
export const entries: EntryGenerator = async () => {
	try {
		const usernames = await fetchAllUsernames(fetch);
		return usernames.map((username) => ({ username }));
	} catch (err) {
		console.error('[perfil] entries: no se pudieron cargar los usuarios:', err);
		return [];
	}
};

export const prerender = true;
